import { create } from 'zustand';
import { type ListarLicitacionesUi } from '../schemas/licitaciones/listar-licitaciones/ui.schema';
import { LicitacionesService } from '../services/licitaciones.service';
import { useLoadingStore } from './loadingStore';
import { useAlertStore } from './alertStore';
import { useFiltersStore } from './filtersStore';

interface FetchOptions {
  silent?: boolean;
}

interface ServerFilters {
  estado?: string;
  search?: string;
  estado_interno?: string;
  codigo_estado?: number;
  postulado?: boolean;
  adjudicado?: boolean;
  no_leidas?: boolean;
  fecha_cierre_desde?: string;
  fecha_cierre_hasta?: string;
  fecha_publicacion_desde?: string;
  fecha_publicacion_hasta?: string;
}

interface LicitacionesState {
  licitaciones: ListarLicitacionesUi[];
  page: number;
  limit: number;
  total: number;
  setLicitaciones: (licitaciones: ListarLicitacionesUi[]) => void;
  setPagination: (page: number, limit: number, total: number) => void;
  fetchLicitaciones: (options?: FetchOptions & { page?: number; limit?: number }) => Promise<void>;
  marcarComoVistoLocal: (licitacionId: number, userId: number) => void;
}

function buildServerFilters(): ServerFilters {
  const {
    activeStatusFilters,
    searchTerm,
    postuladoFilter,
    adjudicadoFilter,
    noLeidasFilter,
    fechaCierreDesde,
    fechaCierreHasta,
    fechaPublicacionDesde,
    fechaPublicacionHasta,
  } = useFiltersStore.getState();
  const filters: ServerFilters = {};

  if (searchTerm.trim()) {
    filters.search = searchTerm.trim();
  }

  const estadoInternos = activeStatusFilters.filter(f => typeof f === 'number') as number[];
  const estadosMP = activeStatusFilters
    .filter(f => typeof f === 'string' && f.startsWith('codigo_estado_'))
    .map(f => Number((f as string).replace('codigo_estado_', '')));

  if (estadoInternos.length === 1) {
    filters.estado_interno = estadoInternos[0].toString();
  }
  if (estadosMP.length === 1) {
    filters.codigo_estado = estadosMP[0];
  }

  if (postuladoFilter === true) {
    filters.postulado = true;
  }
  if (adjudicadoFilter === true) {
    filters.adjudicado = true;
  }
  if (noLeidasFilter === true) {
    filters.no_leidas = true;
  }

  if (fechaCierreDesde) {
    filters.fecha_cierre_desde = fechaCierreDesde;
  }
  if (fechaCierreHasta) {
    filters.fecha_cierre_hasta = fechaCierreHasta;
  }
  if (fechaPublicacionDesde) {
    filters.fecha_publicacion_desde = fechaPublicacionDesde;
  }
  if (fechaPublicacionHasta) {
    filters.fecha_publicacion_hasta = fechaPublicacionHasta;
  }

  return filters;
}

export const useLicitacionesStore = create<LicitacionesState>((set, get) => ({
  licitaciones: [],
  page: 1,
  limit: 20,
  total: 0,
  setLicitaciones: (licitaciones) => set({ licitaciones }),
  setPagination: (page, limit, total) => set({ page, limit, total }),
  marcarComoVistoLocal: (licitacionId: number, userId: number) => {
    const { licitaciones } = get();
    set({
      licitaciones: licitaciones.map((l) =>
        l.id === licitacionId
          ? { ...l, visto: l.visto ? [...l.visto, userId] : [userId] }
          : l
      ),
    });
  },
  fetchLicitaciones: async (options: FetchOptions & { page?: number; limit?: number } = {}) => {
    const { silent = false, page, limit } = options;
    const currentState = get();
    const pageToUse = page ?? currentState.page;
    const limitToUse = limit ?? currentState.limit;
    const filters = buildServerFilters();

    const setLoading = useLoadingStore.getState().setLoading;
    const showAlert = useAlertStore.getState().showAlert;

    if (!silent) {
      setLoading(true);
    }

    try {
      const result = await LicitacionesService.listarLicitaciones(pageToUse, limitToUse, filters);
      set({
        licitaciones: result.data,
        page: result.page,
        limit: result.limit,
        total: result.total
      });

      if (!silent) {
        showAlert('Licitaciones cargadas exitosamente', 'success');
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      showAlert(errorMessage, 'error');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }
}));
